import React, {type ReactNode} from 'react';

import {PanelPopupMode} from '../Panel/types';
import {
  type ExcludeOutliersValues,
  type SinglePointRenderMode,
} from '../WorkspaceDrawer/Settings/types';
import {getTitleFromConfig} from './common';
import {GraphWrapper} from './Graph';
import {
  DEFAULT_GRAPH_LEGEND_POSITION,
  type GraphAxis,
  type GraphRenderProps,
} from './graphRenderProps';
import {getGraphSelection} from './graphSelection';
import {getLegendRunLink} from './graphTooltip';
import {type LinePlotSettings} from './LinePlotSettingsContext';
import {type RunsLinePlotPanelInnerProps} from './types';
import {usePreparedChartSeries} from './usePreparedChartSeries';
import {type GraphZoomInputs} from './zoom/getGraphZoom';
import {usePreparedGraphZoom} from './zoom/usePreparedGraphZoom';

/** Existing application inputs, owned by the compatibility boundary. */
export type GraphProps = RunsLinePlotPanelInnerProps &
  Omit<
    GraphRenderProps,
    | 'title'
    | 'zoom'
    | 'appearance'
    | 'rendering'
    | 'legend'
    | 'loading'
    | 'errors'
    | 'data'
    | 'series'
    | 'seriesStylesById'
    | 'seriesMetadataById'
    | 'axis'
    | 'config'
    | 'plotSettings'
    | 'fallbacks'
  > & {
    disableRunLinks?: boolean;
    panelTitle: {
      isFullScreen: boolean;
      makeHighlightSearchTitle: ((title: string) => ReactNode) | undefined;
      setPanelPopupMode: (mode: PanelPopupMode) => void;
    };
    zoom: GraphZoomInputs;
    zoomUsage: {isFullScreen: boolean; isInReport: boolean};
    plotSettings: LinePlotSettings;
    display: {
      excludeOutliers: ExcludeOutliersValues | undefined;
      showLegend: boolean | undefined;
      singlePointRenderMode: SinglePointRenderMode | undefined;
    };
  };

/** Owns legacy snapshot preparation and selects the renderer's consumed fields. */
export function GraphAdapter(props: GraphProps) {
  const {series, seriesStylesById, seriesMetadataById} = usePreparedChartSeries(
    props.lines
  );
  const controlledZoom = usePreparedGraphZoom(props, series);
  function resetZoom() {
    controlledZoom.onZoom({intent: {shouldZoomX: false, shouldZoomY: false}});
  }
  const zoom: GraphRenderProps['zoom'] = {
    isActive: controlledZoom.activeZoom,
    isZooming: controlledZoom.zooming,
    isZoomedXMax: controlledZoom.isZoomedXMax,
    isZoomedXMin: controlledZoom.isZoomedXMin,
    onReset: resetZoom,
    onZoom: controlledZoom.onZoom,
    xDomain: controlledZoom.xDomain,
    yDomain: controlledZoom.yDomain,
  };
  function openPanelEditor() {
    props.panelTitle.setPanelPopupMode(PanelPopupMode.EDIT);
  }
  const title: GraphRenderProps['title'] = {
    text: getTitleFromConfig(props.config),
    renderHighlightedText: props.panelTitle.isFullScreen
      ? undefined
      : props.panelTitle.makeHighlightSearchTitle,
    onActivate: openPanelEditor,
  };
  const status: {ready: boolean; loading: boolean; hasError?: boolean} = {
    ready: props.data.histories != null,
    loading: props.loading,
    hasError: props.hasError,
  };
  const selection = getGraphSelection(props.config);
  const axis: GraphAxis = {
    xKey: props.plotSettings.xAxis,
    xIsDatetime: props.plotSettings.xAxisIsDatetime,
    xTitle: props.config.xAxisTitle,
    xScale: props.plotSettings.xLogScale
      ? ('log' as const)
      : ('linear' as const),
    yTitle: props.config.yAxisTitle,
    yScale: props.plotSettings.yLogScale
      ? ('log' as const)
      : ('linear' as const),
    outlierPolicy: props.display.excludeOutliers,
  };
  const appearance: GraphRenderProps['appearance'] = {
    fontSize: props.config.fontSize ?? 'auto',
    isDarkMode: props.plotSettings.isDarkMode,
  };
  const rendering: GraphRenderProps['rendering'] = {
    exportMode: props.exportMode ?? false,
    forceReactVis: props.plotSettings.forceReactVis === true,
    isFullFidelity: props.plotSettings.isFullFidelity,
    pointStrategy: props.plotSettings.pointVisualizationMethod,
    plotType: props.plotSettings.plotType,
    allowUPlot: props.plotSettings.uplotEnabled,
  };
  const legend: GraphRenderProps['legend'] = {
    position: props.config.legendPosition ?? DEFAULT_GRAPH_LEGEND_POSITION,
    show: props.display.showLegend,
    colorPalette: props.plotSettings.colorPaletteOption,
    colorRunNames: props.plotSettings.colorRunNames,
    isSingleRun: props.plotSettings.isSingleRun,
    onHighlight: (runId, metricName) =>
      props.highlight.setHighlight('run:name', runId, metricName),
    resolveRunLink: props.disableRunLinks ? undefined : getLegendRunLink,
  };
  const plotSettings: GraphRenderProps['plotSettings'] = {
    colorPaletteOption: props.plotSettings.colorPaletteOption,
    xAxisFormat: props.plotSettings.xAxisFormat,
    colorRunNames: props.plotSettings.colorRunNames,
    displayFullRunName: props.plotSettings.displayFullRunName,
    entityName: props.plotSettings.entityName,
    getNamedWorkspaceSearch: props.plotSettings.getNamedWorkspaceSearch,
    groupingDepth: props.plotSettings.groupingDepth,
    hasMediaPanel: props.plotSettings.hasMediaPanel,
    highlightedCompanionRunOnly: props.plotSettings.highlightedCompanionRunOnly,
    isAnyRunsetGrouped: props.plotSettings.isAnyRunsetGrouped,
    isMultiMetric: props.plotSettings.isMultiMetric,
    isSingleRun: props.plotSettings.isSingleRun,
    limit: props.plotSettings.limit,
    onSnapMediaPanelsToStep: props.plotSettings.onSnapMediaPanelsToStep,
    projectName: props.plotSettings.projectName,
    showXRange: props.plotSettings.showXRange,
    singleRunNavParams: props.plotSettings.singleRunNavParams,
    tooltipNumberOfRuns: props.plotSettings.tooltipNumberOfRuns,
  };
  const fallbacks: GraphRenderProps['fallbacks'] = {
    barYMin: props.config.yAxisMin,
    barYMax: props.config.yAxisMax,
    singlePointStyle: props.display.singlePointRenderMode,
  };
  const loading: GraphRenderProps['loading'] = {
    isActive: status.loading,
    isReady: status.ready,
    regexSettlingKey: selection.regexDataKey,
  };
  const errors: GraphRenderProps['errors'] = {
    hasUnexpectedError: status.hasError,
    hasExpressions: selection.hasExpressions,
    metricKeys: selection.metrics,
    savedXKey: props.config.xAxis,
  };
  return (
    <GraphWrapper
      axis={axis}
      appearance={appearance}
      rendering={rendering}
      cappedPanel={props.cappedPanel}
      config={props.config}
      data={props.data}
      legend={legend}
      highlight={props.highlight}
      issueLines={props.issueLines}
      issueMarkers={props.issueMarkers}
      series={series}
      seriesStylesById={seriesStylesById}
      seriesMetadataById={seriesMetadataById}
      panelId={props.panelId}
      title={title}
      plotSettings={plotSettings}
      readOnly={props.readOnly}
      runNameTruncationType={props.runNameTruncationType}
      samplingWarning={props.samplingWarning}
      sizeRef={props.sizeRef}
      zoom={zoom}
      fallbacks={fallbacks}
      loading={loading}
      errors={errors}
    />
  );
}
